import {
  useInfiniteQuery,
  useQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import {
  QueryContentMode,
  QueryTarget,
  type SubmitStructuredQueryRequest,
} from "@/shared/domain/query";
import { PaginatedResponse } from "@/shared/http";
import { type SearchQueryBody } from "@/types";

import { DatasetRecord } from "../DatasetRecord";

interface UseDatasetsQueryOptions {
  orgId: string | undefined;
  query?: SearchQueryBody;
  pageSize?: number;
}

/**
 * Query key factory for dataset queries. Shared by `useDatasetsQuery` and
 * any mutation hooks that need to invalidate dataset lists (e.g.
 * `useCreateDataset`), so the read and write sides cannot drift.
 */
export const datasetQueryKeys = {
  all: ["datasets"] as const,
  orgTags: (orgId: string) => [...datasetQueryKeys.all, "tags", orgId] as const,
  orgTagSearch: (orgId: string, search: string) =>
    [...datasetQueryKeys.orgTags(orgId), "search", search] as const,
  lists: () => [...datasetQueryKeys.all, "list"] as const,
  list: (orgId: string | undefined, query?: SearchQueryBody) => {
    if (query) {
      return [...datasetQueryKeys.lists(), orgId, query];
    }
    return [...datasetQueryKeys.lists(), orgId];
  },
};

function buildSubmitRequest(
  query: SearchQueryBody,
): SubmitStructuredQueryRequest {
  return {
    query,
    target: QueryTarget.Datasets,
    content_mode: QueryContentMode.RecordOnly,
  };
}

/**
 * Hook to query datasets with pagination support.
 *
 * Internally submits a structured query via QueryService, then fetches
 * paginated results using the returned query_id.
 */
export function useDatasetsQuery(options: UseDatasetsQueryOptions) {
  const { queryService } = useDomainServices();
  const { orgId, query = {}, pageSize = 50 } = options;

  const submitQuery = useQuery({
    queryKey: datasetQueryKeys.list(orgId, query),
    queryFn: () => {
      const request = buildSubmitRequest(query);
      return queryService.submitStructured(request, {
        resourceOwnerId: orgId!,
      });
    },
    enabled: !!orgId,
  });

  const queryId = submitQuery.data?.query_id;

  const resultsQuery = useInfiniteQuery({
    queryKey: ["queryResults", queryId, pageSize],
    queryFn: ({ pageParam, signal }) => {
      return queryService.getQueryResults<DatasetRecord>(queryId!, {
        abortSignal: signal,
        nextPageToken: pageParam,
        pageSize,
      });
    },
    select: (data) => {
      return data.pages.flatMap((page) => page.items);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedResponse<DatasetRecord>) =>
      lastPage.next_token,
    enabled: !!queryId,
    staleTime: Infinity,
  });

  return {
    ...resultsQuery,
    isLoading: submitQuery.isLoading || resultsQuery.isLoading,
    error: submitQuery.error || resultsQuery.error,
  };
}

/**
 * Hook to query datasets with pagination support using Suspense.
 *
 * Uses React Query's useSuspenseQuery/useSuspenseInfiniteQuery which
 * integrate with React Suspense for loading states and Error Boundaries
 * for error handling.
 */
export function useInfiniteSuspenseDatasetsQuery(
  options: UseDatasetsQueryOptions,
) {
  const { queryService } = useDomainServices();
  const { orgId, query = {}, pageSize = 50 } = options;

  const submitQuery = useSuspenseQuery({
    queryKey: datasetQueryKeys.list(orgId, query),
    queryFn: () => {
      const request = buildSubmitRequest(query);
      return queryService.submitStructured(request, {
        resourceOwnerId: orgId!,
      });
    },
  });

  const queryId = submitQuery.data.query_id;

  const resultsQuery = useSuspenseInfiniteQuery({
    queryKey: ["queryResults", queryId],
    queryFn: ({ pageParam, signal }) => {
      return queryService.getQueryResults<DatasetRecord>(queryId, {
        abortSignal: signal,
        nextPageToken: pageParam,
        pageSize,
      });
    },
    select: (data): DatasetRecord[] => {
      return data.pages.flatMap((page) => page.items);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedResponse<DatasetRecord>) =>
      lastPage.next_token,
    staleTime: Infinity,
  });

  return resultsQuery;
}
